/**
 * Table Components — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - Semantic HTML table structure
 * - Striped rows option
 * - Hoverable rows option
 * - Dense/comfortable spacing
 * - Uses semantic theme tokens
 * - Square geometry (border-radius: 0)
 */

import React, { HTMLAttributes, ThHTMLAttributes, TdHTMLAttributes } from 'react';

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  striped?: boolean;
  hoverable?: boolean;
  dense?: boolean;
  children: React.ReactNode;
}

export function Table({
  striped = false,
  hoverable = false,
  dense = false,
  className = '',
  children,
  ...props
}: TableProps) {
  return (
    <div className="overflow-x-auto border border-[var(--reviewer-border)]">
      <table
        className={`
          w-full
          font-mono text-[var(--reviewer-text-sm)]
          border-collapse
          ${className}
        `}
        {...props}
      >
        {children}
      </table>
    </div>
  );
}

export interface TableHeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

export function TableHead({ className = '', children, ...props }: TableHeadProps) {
  return (
    <thead
      className={`
        bg-[var(--reviewer-bg-tertiary)]
        border-b-2 border-[var(--reviewer-border-strong)]
        ${className}
      `}
      {...props}
    >
      {children}
    </thead>
  );
}

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
  striped?: boolean;
  hoverable?: boolean;
}

export function TableBody({
  striped = false,
  hoverable = false,
  className = '',
  children,
  ...props
}: TableBodyProps) {
  return (
    <tbody className={className} {...props}>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child) && child.type === TableRow) {
          return React.cloneElement(child as React.ReactElement<TableRowProps>, {
            striped: striped && index % 2 === 1,
            hoverable,
          });
        }
        return child;
      })}
    </tbody>
  );
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
  striped?: boolean;
  hoverable?: boolean;
}

export function TableRow({
  striped = false,
  hoverable = false,
  className = '',
  children,
  ...props
}: TableRowProps) {
  return (
    <tr
      className={`
        border-b border-[var(--reviewer-border)]
        ${striped ? 'bg-[var(--reviewer-bg-elevated)]' : ''}
        ${hoverable ? 'hover:bg-[var(--reviewer-surface-hover)] transition-colors' : ''}
        ${className}
      `}
      {...props}
    >
      {children}
    </tr>
  );
}

export interface TableHeaderProps extends ThHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  dense?: boolean;
}

export function TableHeader({
  dense = false,
  className = '',
  children,
  ...props
}: TableHeaderProps) {
  return (
    <th
      className={`
        ${dense ? 'px-2 py-1' : 'px-4 py-3'}
        text-left
        text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)]
        font-medium
        uppercase tracking-wider
        ${className}
      `}
      {...props}
    >
      {children}
    </th>
  );
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  dense?: boolean;
}

export function TableCell({
  dense = false,
  className = '',
  children,
  ...props
}: TableCellProps) {
  return (
    <td
      className={`
        ${dense ? 'px-2 py-1' : 'px-4 py-3'}
        text-[var(--reviewer-text)]
        ${className}
      `}
      {...props}
    >
      {children}
    </td>
  );
}
